import type { LoginResponseDto, LoginResultDto, LogoutResponseDto, MeDto } from '@fas/types';
import { ApiCookies, ApiHeaders, isMfaChallenge } from '@fas/types';
import { api, ApiError, apiRequest, buildUrl, readCookie, refreshSession } from './api';
import { useAuthStore } from './auth-store';
import { readyDeviceId } from './device';
import { resetDeviceChallenge } from './device-proof';
import { forgetPushSubscription, syncPushSubscription } from './push';
import { queryClient } from './query-client';

const RETRY_MS = 15_000;
let retryTimer: ReturnType<typeof setInterval> | null = null;

/** Retries restoring the session when the connection returns (or every 15 s while the server is down). */
function retryWhenReachable(): void {
  if (retryTimer || typeof window === 'undefined') return;
  const retry = () => {
    if (retryTimer) clearInterval(retryTimer);
    retryTimer = null;
    window.removeEventListener('online', retry);
    // Next tick: the failed refresh attempt is released only after the current one.
    setTimeout(() => void retrySession(), 0);
  };
  window.addEventListener('online', retry);
  retryTimer = setInterval(() => {
    if (navigator.onLine) retry();
  }, RETRY_MS);
}

/** Restores a session on page load from the refresh cookie (silent re-login). */
export async function bootstrapSession(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.status !== 'loading') return;
  const outcome = await refreshSession();
  if (outcome === 'ok') {
    try {
      const me = await api.get<MeDto>('/auth/me');
      useAuthStore.getState().setSession(useAuthStore.getState().accessToken!, me);
      void syncPushSubscription();
      return;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NETWORK_ERROR') {
        useAuthStore.setState({ status: 'offline' });
        retryWhenReachable();
        return;
      }
    }
  } else if (outcome === 'unreachable') {
    // Signed in, but the server cannot be reached: say so and reconnect later instead of
    // showing a sign-in form that cannot work (§57 "basic resilience").
    useAuthStore.setState({ status: 'offline' });
    retryWhenReachable();
    return;
  }
  useAuthStore.getState().clear();
}

/** Tries again to restore a session that could not be checked while offline. */
export async function retrySession(): Promise<void> {
  if (useAuthStore.getState().status !== 'offline') return;
  useAuthStore.setState({ status: 'loading' });
  await bootstrapSession();
}

/** A password sign-in either completes, or asks for the second step's code (ADR-068). */
export type SignInResult = { status: 'signed-in'; me: MeDto } | { status: 'code-required'; mfaToken: string };

export async function signIn(email: string, password: string): Promise<SignInResult> {
  resetDeviceChallenge();
  const { data } = await apiRequest<LoginResultDto>('POST', '/auth/login', {
    body: { email, password, deviceId: await readyDeviceId() },
    auth: false,
  });
  if (isMfaChallenge(data)) return { status: 'code-required', mfaToken: data.mfaToken };
  return { status: 'signed-in', me: await startSession(data.accessToken) };
}

/** The second step: a code from the authenticator app, or a recovery code. */
export async function signInWithCode(mfaToken: string, code: string): Promise<MeDto> {
  const { data } = await apiRequest<LoginResponseDto>('POST', '/auth/login/mfa', {
    body: { mfaToken, code, deviceId: await readyDeviceId() },
    auth: false,
  });
  return startSession(data.accessToken);
}

async function startSession(accessToken: string): Promise<MeDto> {
  useAuthStore.getState().setAccessToken(accessToken);
  const me = await api.get<MeDto>('/auth/me');
  useAuthStore.getState().setSession(accessToken, me);
  // Push follows the sign-in session: re-register this browser if its owner turned push on.
  void syncPushSubscription();
  return me;
}

/** Leaving the application for another site (the identity provider's sign-out page). */
export const browser = {
  leave(url: string): void {
    window.location.assign(url);
  },
};

/** Signs out here; a single sign-on session then continues at the identity provider's sign-out page. */
export async function signOut(): Promise<void> {
  const csrf = readCookie(ApiCookies.CSRF_TOKEN);
  // While still signed in; never hold up signing out for it.
  await Promise.race([forgetPushSubscription(), new Promise((resolve) => setTimeout(resolve, 1_500))]);
  let providerLogout: string | null = null;
  try {
    const res = await fetch(buildUrl('/auth/logout'), {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { [ApiHeaders.CSRF_TOKEN]: csrf } : {},
    });
    const body = (await res.json().catch(() => null)) as { data?: LogoutResponseDto } | null;
    providerLogout = body?.data?.ssoLogoutUrl ?? null;
  } catch {
    // Offline: the session ends here anyway.
  } finally {
    useAuthStore.getState().clear();
    queryClient.clear();
    resetDeviceChallenge();
  }
  if (providerLogout) browser.leave(providerLogout);
}

export async function reloadMe(): Promise<void> {
  const me = await api.get<MeDto>('/auth/me');
  useAuthStore.getState().setMe(me);
}
